import React, { useState } from 'react';
import { Form, Input, DatePicker, Radio, Row, Col, Typography, Divider, Select, Button, Modal, Checkbox } from 'antd';
import dayjs from 'dayjs';
import countryList from 'react-select-country-list';
import { useSettings } from "../../contexts/settingsContext";
import { RightOutlined } from '@ant-design/icons';
import parse from 'html-react-parser';
import termsPDF from '../../TermsAndConditions/SEMI-FINAL Rules and Regulations 2026 - SOLO.pdf'
import infoPDF from '../../TermsAndConditions/SACOPA Official Contestant - Information Package 2026 v2.pdf';  
import contractPDF from '../../TermsAndConditions/SACOPA Nationals - OFFICIAL CONTRACT 2026.pdf'; 

const { Title } = Typography;
const { Option } = Select;

const southAfricanProvinces = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'Northern Cape',
  'North West',
  'Western Cape',
];

const ContestantForm = ({ onNext, submitting, user, enforceAgeRestriction = true, provinces }) => {
    const [form] = Form.useForm();
    const [allergies, setAllergies] = useState(false);
    const [isIdNumberEntered, setIsIdNumberEntered] = useState(false);
    const [isPassportNumberEntered, setIsPassportNumberEntered] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const { state: settings } = useSettings();
    const minimumAge = Number(settings?.minimumAge ?? 0);
    const [termsAccepted, setTermsAccepted] = useState(false);  
    const [infoAccepted, setInfoAccepted] = useState(false);  
    const [contractAccepted, setContractAccepted] = useState(false);  
    const [isPreviousContestant, setIsPreviousContestant] = useState(false);  
    
    const [isModalVisible, setIsModalVisible] = useState(false);  
    const [modalPDF, setModalPDF] = useState(null); // To decide which PDF to show  
    
    const canSubmit = termsAccepted;  

    const validatePassword = (_, value) => {
        if (!value) {
            return Promise.reject(new Error('Please input your password'));
        }
        if (value.length < 7) {
            return Promise.reject(new Error('Password must be at least 7 characters long'));
        }
        if (value.toLowerCase().includes('password')) {
            return Promise.reject(new Error('Password cannot contain "password"'));
        }
        const specialCharacterRegex = /[!@#$%^&*(),.?":{}|<>]/;
        if (!specialCharacterRegex.test(value)) {
            return Promise.reject(new Error('Password must contain at least one special character'));
        }
        return Promise.resolve();
    };

    const validateConfirmPassword = (_, value) => {
        if (!value || form.getFieldValue('password') === value) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('The two passwords do not match!'));
    };

    const validateSouthAfricanIdNumber = (_, value) => {
        if (value.length !== 13) {
            return Promise.reject(new Error('ID number must be 13 digits long'));
        }
        const year = parseInt(value.slice(0, 2), 10);
        const month = parseInt(value.slice(2, 4), 10);
        const day = parseInt(value.slice(4, 6), 10);
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return Promise.reject(new Error('Invalid date in ID number'));
        }
        const gender = parseInt(value.slice(6, 10), 10);
        if (gender < 0 || gender > 9999) {
            return Promise.reject(new Error('Invalid gender digits in ID number'));
        }
        const citizenship = parseInt(value.slice(10, 11), 10);
        if (citizenship !== 0 && citizenship !== 1) {
            return Promise.reject(new Error('Invalid citizenship digit in ID number'));
        }
        const luhnCheck = (num) => {
            let sum = 0;
            for (let i = 0; i < num.length; i++) {
                let digit = parseInt(num[i], 10);
                if (i % 2 === num.length % 2) {
                    digit *= 2;
                    if (digit > 9) {
                        digit -= 9;
                    }
                }
                sum += digit;
            }
            return sum % 10 === 0;
        };
        if (!luhnCheck(value)) {
            return Promise.reject(new Error('Invalid digit in ID number. Please verify your ID number is correct.'));
        }
        return Promise.resolve();
    };

    const handleDateChange = (date) => {
        if (date) {
            const age = dayjs().diff(date, 'year');
            if (enforceAgeRestriction && age < minimumAge) {
                form.setFields([
                    {
                        name: 'date_of_birth',
                        errors: [`You must be at least ${minimumAge} years old`],
                    },
                ]);
            } else {
                form.setFieldsValue({ age });
            }
        }
    };

    const handleIdNumberChange = (e) => {
        const value = e.target.value;
        setIsIdNumberEntered(!!value);
        if (selectedCountry === 'South Africa' && value.length === 13) {
            const year = parseInt(value.slice(0, 2), 10);
            const month = parseInt(value.slice(2, 4), 10) - 1;
            const day = parseInt(value.slice(4, 6), 10);
            const currentYear = dayjs().year();
            const fullYear = year + (year > currentYear % 100 ? 1900 : 2000);
            const dateOfBirth = dayjs(new Date(fullYear, month, day));
            const age = dayjs().diff(dateOfBirth, 'year');
            form.setFieldsValue({ date_of_birth: dateOfBirth, age });
        }
    };

    const handlePassportNumberChange = (e) => {
        const value = e.target.value;
        setIsPassportNumberEntered(!!value);
    };

    const handleCountryChange = (value) => {
        setSelectedCountry(value);
        form.setFieldsValue({ id_number: '', passport_number: '' });
        setIsIdNumberEntered(false);
        setIsPassportNumberEntered(false);
    };

    const handleAllergiesChange = (e) => {
        const value = e.target.value;
        setAllergies(value === true);
        form.setFieldsValue({ allergies: value });
    };

    const onFinish = (values) => {  
        if (!canSubmit) {  
          return;  
        }  
        onNext(values);  
      };  

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleAccept = () => {
        setTermsAccepted(true);
        setIsModalVisible(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const validateUniqueIdNumber = async (_, value) => {
  // Only check when there *is* a value and it's 13 digits (for SA IDs)
  if (!value || value.length !== 13) {
    return Promise.resolve();
  }

  try {
    // Adjust the URL if you have an /api prefix
    const response = await fetch(`/api/contestants/check-id/${value}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // You can choose to pass here instead of failing, if you don't want to block on server issues
      return Promise.reject(
        new Error('Could not validate ID number. Please try again.')
      );
    }

    const data = await response.json();

    if (data.exists) {
      return Promise.reject(
        new Error('A contestant with this ID number already exists')
      );
    }

    return Promise.resolve();
  } catch (err) {
    return Promise.reject(
      new Error('Could not validate ID number. Please try again.')
    );
  }
};


    return (
        <div>
            <Form
                form={form}
                className='contestant_form'
                name="contestant"
                initialValues={{ remember: true, age: null }}
                layout="vertical"
                onFinish={onFinish}
                scrollToFirstError
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            name="first_name"
                            label="First Name"
                            rules={[{ required: true, message: 'Please input your first name!' }]}
                        >
                            <Input placeholder="First Name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            name="last_name"
                            label="Last Name"
                            rules={[{ required: true, message: 'Please input your last name!' }]}
                        >
                            <Input placeholder="Last Name" />
                        </Form.Item>
                    </Col>
                </Row> 
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.country"
                            label="Country of Citizenship"
                            rules={[{ required: true, message: 'Please input your country of citizenship!' }]}
                        >
                            <Select  
                                placeholder="Select Country"  
                                onChange={handleCountryChange}  
                                value={selectedCountry}  
                                showSearch  
                                optionFilterProp="children"  
                                autoComplete="none"  
                                filterOption={(input, option) =>  
                                    option.children.toLowerCase().startsWith(input.toLowerCase())  
                                }  
                            >  
                                {countryList().getData().map(country => (  
                                    <Option key={country.value} value={country.label}>{country.label}</Option>  
                                ))}  
                            </Select>  
                        </Form.Item>
                    </Col>
                    <Row gutter={[16, 8]}>  
                        <Col xs={24} sm={24} md={24}>  
                            <Form.Item label="Have you entered SACOPA before?" style={{ marginBottom: 0 }}>  
                            <Radio.Group  
                                value={isPreviousContestant}  
                                onChange={e => setIsPreviousContestant(e.target.value)}  
                            >  
                                <Radio value={false}>No, I am a new contestant</Radio>  
                                <Radio value={true}>Yes, I have a contestant number</Radio>  
                            </Radio.Group>  
                            </Form.Item>  
                        </Col>  
                        </Row>  
                        
                        {isPreviousContestant && (  

                            <Form.Item  
                                name="contestant_number"  
                                label="Previous Contestant Number"  
                                rules={[  
                                { required: true, message: "Please input your previous contestant number!" },  
                                { pattern: /^[0-9]+$/, message: 'Contestant number must be numerical' }  
                                ]}  
                            >  
                                <Input placeholder="Contestant Number" />  
                            </Form.Item>  
                        )}  
                    {selectedCountry && (
                        <>
                            {selectedCountry === 'South Africa' && (
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                name="id_number"
                                label="ID Number"
                                validateTrigger="onBlur"
                                rules={[
                                    {
                                    required: !isPassportNumberEntered,
                                    message: 'Please input your ID number!',
                                    },
                                    {
                                    validator: selectedCountry === 'South Africa'
                                        ? validateSouthAfricanIdNumber
                                        : null,
                                    },
                                    {
                                    validator: validateUniqueIdNumber,
                                    },
                                ]}
                                >
                                <Input
                                    placeholder="ID Number"
                                    onChange={handleIdNumberChange}
                                />
                                </Form.Item>
                            </Col>
                            )}
                            {(selectedCountry !== 'South Africa' || settings.passportRequired) && (
                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item
                                        name="passport_number"
                                        label="Passport Number"
                                        rules={[{ required: !isIdNumberEntered, message: 'Please input your passport number!' }]}
                                    >
                                        <Input placeholder="Passport Number" onChange={handlePassportNumberChange} />
                                    </Form.Item>
                                </Col>
                            )}
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="date_of_birth"
                                    label="Date of Birth"
                                    rules={[{ required: !isIdNumberEntered, message: 'Please select your date of birth!' }]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        onChange={handleDateChange}
                                        disabledDate={current => enforceAgeRestriction && current && current > dayjs().subtract(minimumAge, 'year')}
                                        disabled={isIdNumberEntered || selectedCountry === 'South Africa'}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="age"
                                    label="Age"
                                    rules={[{ required: true, message: 'Please input your age!' }]}>
                                    <Input placeholder="Age" disabled />
                                </Form.Item>
                            </Col>
                        </>
                    )}
                </Row>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24}>
                        <Form.Item
                            name="gender"
                            label="Gender"
                            rules={[{ required: true, message: 'Please select your gender!' }]}
                        >
                            <Radio.Group options={[
                                { label: 'Male', value: 'Male' },
                                { label: 'Female', value: 'Female' },
                            ]} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={24}>
                        <Form.Item
                            name="allergies"
                            label="Do you Have Any Allergies?"
                            rules={[{ required: true, message: 'Please select if you have any allergies!' }]}
                        >
                            <Radio.Group onChange={handleAllergiesChange} options={[
                                { label: 'Yes', value: true },
                                { label: 'No', value: false }
                            ]} />
                        </Form.Item>
                    </Col>
                    {allergies && (
                        <Col xs={24} sm={24} md={24}>
                            <Form.Item
                                name="allergies_specification"
                                label="Allergies Specification"
                                rules={[{ required: allergies, message: 'Please specify your allergies!' }]}
                            >
                                <Input placeholder="Allergies Specification" />
                            </Form.Item>
                        </Col>
                    )}
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="academic_institution"
                            label="Academic Institution"
                            rules={[{ required: true, message: 'Please input your academic institution!' }]}
                        >
                            <Input placeholder="Name of school, college or university (if applicable)" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="provincial_director"
                            label="Provincial Director's Name"
                            rules={[{ required: true, message: "Please input your provincial director's name!" }]}
                        >
                            <Input placeholder="Provincial Director's Name (if applicable)" />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider />
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Title level={4}>Contact Information</Title>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="cell_number"
                            label="Cell Number"
                            rules={[{ required: true, message: 'Please input your cell number!' }, { pattern: /^[0-9]{10}$/, message: 'Please input a valid cell number!' }]}
                        >
                            <Input placeholder="Cell Number" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="work_number"
                            label="Work Number"
                            rules={[{ message: 'Please input your work number!' }, { pattern: /^[0-9]{10}$/, message: 'Please input a valid work number!' }]}
                        >
                            <Input placeholder="Work Number" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[{ required: true, type: 'email', message: 'Please input a valid email!' }]}
                        >
                            <Input placeholder="Email" />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider />
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Title level={4}>Address Information</Title>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.street_1"
                            label="Street 1"
                            rules={[{ required: true, message: 'Please input your street!' }]}
                        >
                            <Input placeholder="Street 1" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.street_2"
                            label="Street 2"
                        >
                            <Input placeholder="Street 2" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.street_3"
                            label="Street 3"
                        >
                            <Input placeholder="Street 3" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.city"
                            label="City"
                            rules={[{ required: true, message: 'Please input your city!' }]}
                        >
                            <Input placeholder="City" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.province"
                            label="Province/State"
                            rules={[{ required: true, message: 'Please input your province/state!' }]}
                        >
                            <Select
                              placeholder="Select Province"
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                              }
                            >
                              {southAfricanProvinces.map((province) => (
                                <Option key={province} value={province}>
                                  {province}
                                </Option>
                              ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.postal_code"
                            label="Postal Code"
                            rules={[{ required: true, message: 'Please input your postal code!' }]}
                        >
                            <Input placeholder="Postal Code" />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider />
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Title level={4}>Coach/Parent Information (Type "NO" if not applicable)</Title>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="ice_contact_name"
                            label="Coach/Parent Name"
                            rules={[{ required: true, message: 'Please input your contact name!' }]}
                        >
                            <Input placeholder="Contact Name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="relationship_to_ice"
                            label="Relationship to Contestant"
                            rules={[{ required: true, message: `Please input the contact's relationship to the contestant!` }]}
                        >
                            <Input placeholder="Relationship to Contestant" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="ice_contact_email"
                            label="Coach/Parent Email"
                            rules={[{ required: true, message: 'Please input your contact email!' }]}
                        >
                            <Input placeholder="Contact Email" />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item  
                  name="audition_province"  
                  label="Semi Final Audition Date"  
                  rules={[{ required: true, message: 'Please select your audition date!' }]}  
                >  
                  <Select placeholder="Select your audition date" showSearch optionFilterProp="children">  
                    {provinces && provinces.map(prov => (  
                      <Option key={prov._id} value={prov._id}>  
                        <div>  
                          <strong>{prov.province}</strong>{" "}  
                          <span style={{ color: "#555" }}>{prov.location} - {dayjs(prov.date).format('YYYY-MM-DD')}  </span>  
                        </div>  
                      </Option>  
                    ))}  
                  </Select>  
                </Form.Item> 
                {!user && (
                    <Row gutter={[16, 16]}>
                        <Divider />
                        <Col span={24}>
                            <Title level={4}>Create Password (This password will be used for your account login)</Title>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                            <Form.Item
                                name="password"
                                label="Password"
                                rules={[{ validator: validatePassword }]}
                            >
                                <Input.Password placeholder="Password" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                            <Form.Item
                                name="confirm_password"
                                label="Confirm password"
                                rules={[
                                    { required: true, message: 'Please confirm your password' },
                                    { validator: validateConfirmPassword }
                                ]}
                            >
                                <Input.Password placeholder="Confirm password" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
                <Divider />
                <div className='form_actions'>
                  <Row align="left" style={{ flexDirection: 'column' }}>  
                    <Col>  
                      <Checkbox  
                        checked={termsAccepted}  
                        onChange={e => setTermsAccepted(e.target.checked)}  
                        style={{ marginBottom: 8 }}  
                      >  
                        I accept the{' '}  
                        <Button  
                          type="link"  
                          onClick={() => { setModalPDF(termsPDF); setIsModalVisible(true); }}  
                          style={{ padding: 0 }}  
                        >  
                          semi-finals rules and regulations  
                        </Button>  
                      </Checkbox>  
                    </Col>  
                  </Row>  
                  <Row>
                    <Col span={24} style={{ textAlign: 'right' }}>
                        <Button type="primary" loading={submitting} icon={<RightOutlined />} htmlType="submit" disabled={!canSubmit}>
                            Continue
                        </Button>
                    </Col>
                  </Row>
                </div>
            </Form>
            <Modal  
                title="Terms and Conditions"  
                open={isModalVisible}  
                onOk={() => setIsModalVisible(false)}  
                onCancel={() => setIsModalVisible(false)}  
                footer={  
                    <Button key="close" onClick={() => setIsModalVisible(false)}>  
                      Close  
                    </Button>  
                }  
                width={800}  
            >  
                {modalPDF && (  
                    <iframe  
                      src={modalPDF}  
                      width="100%"  
                      height="700px"  
                      style={{ border: 'none' }}  
                      title="Terms and Conditions PDF"  
                    />  
                )}  
                <Divider />  
            </Modal>  
        </div>
    );
};

export default ContestantForm;
